import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';
import { Purchases, LOG_LEVEL, CustomerInfo, PurchasesOffering, PurchasesPackage } from '@revenuecat/purchases-capacitor';
import { Device } from '@capacitor/device';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

interface PurchasesContextType {
  customerInfo: CustomerInfo | null;
  offerings: PurchasesOffering[] | null;
  isReady: boolean;
  isPro: boolean;
  purchasePackage: (pkg: PurchasesPackage) => Promise<boolean>;
  restorePurchases: () => Promise<boolean>;
  toggleDevPro?: () => void;
  setDevProMode?: (val: boolean) => void;
}

const PurchasesContext = createContext<PurchasesContextType>({} as PurchasesContextType);

export const usePurchases = () => useContext(PurchasesContext);


// Exact identifier from your RevenueCat Dashboard
const ENTITLEMENT_ID = "Airchips Pro";

export const PurchasesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [offerings, setOfferings] = useState<PurchasesOffering[] | null>(null);
  const [rcReady, setRcReady] = useState(false);
  const isReady = rcReady;
  const isDev = Boolean(import.meta.env.DEV || import.meta.env.VITE_IS_DEV_SERVER === 'true');
  const isPrivateBuild = import.meta.env.VITE_PUBLIC_OFFLINE !== 'true';

  const [devProMode, setDevProMode] = useState<boolean>(() => {
    const savedDevProMode = localStorage.getItem('dev_pro_mode');
    return savedDevProMode === null ? isPrivateBuild : savedDevProMode === 'true';
  });

  const setDevPro = (val: boolean) => {
    console.log('[PurchasesContext] Dev Pro set:', val);
    setDevProMode(val);
    localStorage.setItem('dev_pro_mode', val ? 'true' : 'false');
  };

  const toggleDevPro = () => {
    setDevPro(!devProMode);
  };

  useEffect(() => {
    const initPurchases = async () => {
      // 1. Skip on pure web view to prevent errors
      if (!Capacitor.isNativePlatform()) {
        console.log("RevenueCat: Running on web, mocking readiness.");
        if (import.meta.env.VITE_IS_DEV_SERVER === 'true') {
          setOfferings([{
            identifier: 'dev_offering',
            serverDescription: 'Dev Offering',
            availablePackages: [{
              identifier: 'Lifetime',
              packageType: 'LIFETIME' as any,
              product: {
                identifier: 'airchips_pro_lifetime',
                description: 'Supporter Pass',
                title: 'Supporter Pass',
                price: 4.99,
                priceString: '$4.99',
                currencyCode: 'USD',
                introPrice: null
              }
            }]
          } as any]);
        }
        setRcReady(true);
        return;
      }

      try {
        await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
        
        // 2. Initialize with your API key
        const platform = Capacitor.getPlatform();

        let apiKey = '';
        if (platform === 'ios') {
           apiKey = import.meta.env.VITE_REVENUECAT_IOS_KEY || '';
        } else if (platform === 'android') {
           apiKey = import.meta.env.VITE_REVENUECAT_ANDROID_KEY || '';
        }

        if (apiKey) {
          await Purchases.configure({ apiKey });
          
          // 3. Fetch initial Customer Info & Offerings (like "Yearly")
          const info = await Purchases.getCustomerInfo();
          setCustomerInfo(info.customerInfo);

          const fetchedOfferings = await Purchases.getOfferings();
          if (fetchedOfferings.current) {
            setOfferings([fetchedOfferings.current]);
          }

          // 4. Attach Customer Info Update Listener
          Purchases.addCustomerInfoUpdateListener((info: any) => {
            setCustomerInfo(info.customerInfo || info);
          });
        } else {
          console.warn('RevenueCat API key not found.');
        }
      } catch (error) {
        console.error('Failed to initialize RevenueCat:', error);
      } finally {
        setRcReady(true);
      }
    };

    initPurchases();
  }, []);

  const isRealPro = customerInfo?.entitlements.active[ENTITLEMENT_ID] !== undefined;
  const isPro = isDev ? devProMode : isPrivateBuild || isRealPro;

  const purchasePackage = async (pkg: PurchasesPackage) => {
    if (!Capacitor.isNativePlatform()) {
      console.log('Mocking purchase package on web...');
      await new Promise(resolve => setTimeout(resolve, 800));
      return true;
    }
    try {
      const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
      setCustomerInfo(customerInfo);
      return customerInfo.entitlements.active[ENTITLEMENT_ID] !== undefined;
    } catch (e: any) {
      if (!e.userCancelled) console.error('Purchase failed:', e);
      return false;
    }
  };

  const restorePurchases = async () => {
    try {
      const { customerInfo } = await Purchases.restorePurchases();
      setCustomerInfo(customerInfo);
      return customerInfo.entitlements.active[ENTITLEMENT_ID] !== undefined;
    } catch (e) {
      console.error('Restore failed:', e);
      return false;
    }
  };

  return (
    <PurchasesContext.Provider value={{ 
      customerInfo, 
      offerings, 
      isReady, 
      isPro, 
      purchasePackage, 
      restorePurchases, 
      toggleDevPro,
      setDevProMode: setDevPro
    }}>
      {children}
    </PurchasesContext.Provider>
  );
};
